import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { adminApi, type PaymentListDto } from '@/api/admin'
import { useTopBar } from '@/contexts/TopBarContext'
import { ChevronUp, ChevronDown, ChevronsUpDown, Search, X, RotateCcw, Loader2 } from 'lucide-react'
import { usePagination } from '@/hooks/usePagination'
import { TablePagination } from '@/components/shared/TablePagination'
import { useMoneyFor } from '@/hooks/useMoney'
import { formatMoney } from '@/lib/money'
import { useAdminDates } from '@/hooks/useDates'

type SortKey = 'createdAt' | 'description' | 'payer' | 'amount'
type SortDir = 'asc' | 'desc'

function SortIcon({ col, sortKey, dir }: { col: SortKey; sortKey: SortKey; dir: SortDir }) {
  if (col !== sortKey) return <ChevronsUpDown size={13} className="text-gray-300 ml-1 inline" />
  return dir === 'asc'
    ? <ChevronUp size={13} className="text-indigo-500 ml-1 inline" />
    : <ChevronDown size={13} className="text-indigo-500 ml-1 inline" />
}

function Th({ label, col, sortKey, dir, onSort, className }: {
  label: string; col: SortKey; sortKey: SortKey; dir: SortDir
  onSort: (col: SortKey) => void; className?: string
}) {
  return (
    <th
      className={`px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:text-gray-700 whitespace-nowrap ${className ?? ''}`}
      onClick={() => onSort(col)}
    >
      {label}<SortIcon col={col} sortKey={sortKey} dir={dir} />
    </th>
  )
}

const SOURCE_LABEL: Record<string, string> = { Invoice: 'Invoice', SessionBooking: 'Session' }

function refundState(p: PaymentListDto): { label: string; cls: string } {
  if (p.refundedAmount <= 0) return { label: 'Paid', cls: 'text-green-700 bg-green-50 border-green-200' }
  if (p.refundedAmount < p.amount) return { label: 'Part refunded', cls: 'text-amber-700 bg-amber-50 border-amber-200' }
  return { label: 'Refunded', cls: 'text-gray-500 bg-gray-100 border-gray-200' }
}

export default function PaymentsPage() {
  const { formatDate } = useAdminDates()
  const fmtDate = (d: string) => {
    return formatDate(d, { day: 'numeric', month: 'short', year: 'numeric' })
  }
  useTopBar(<span className="text-sm font-medium text-gray-900">Payments</span>)
  const qc = useQueryClient()

  const { data: payments = [], isLoading } = useQuery({
    queryKey: ['payments'],
    queryFn: () => adminApi.getPayments().then(r => r.data),
  })

  const [search, setSearch] = useState('')
  const [sourceFilter, setSourceFilter] = useState<'' | 'Invoice' | 'SessionBooking'>('')
  const [sortKey, setSortKey] = useState<SortKey>('createdAt')
  const [sortDir, setSortDir] = useState<SortDir>('desc')
  const [refundTarget, setRefundTarget] = useState<PaymentListDto | null>(null)

  const handleSort = (col: SortKey) => {
    if (sortKey === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortKey(col); setSortDir('desc') }
  }

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim()
    return payments
      .filter(p => !sourceFilter || p.source === sourceFilter)
      .filter(p => !q || [p.description, p.payer ?? ''].some(v => v.toLowerCase().includes(q)))
      .sort((a, b) => {
        let cmp = 0
        switch (sortKey) {
          case 'createdAt':   cmp = a.createdAt.localeCompare(b.createdAt); break
          case 'description': cmp = a.description.localeCompare(b.description); break
          case 'payer':       cmp = (a.payer ?? '').localeCompare(b.payer ?? ''); break
          case 'amount':      cmp = a.amount - b.amount; break
        }
        return sortDir === 'asc' ? cmp : -cmp
      })
  }, [payments, search, sourceFilter, sortKey, sortDir])

  const { paged, page, setPage, totalPages, total, pageSize, start } = usePagination(filtered)

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="text-sm text-gray-400">{filtered.length} of {payments.length}</div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-56">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search description or payer…"
            className="w-full pl-9 pr-8 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer">
              <X size={14} />
            </button>
          )}
        </div>
        <div className="flex gap-1.5">
          {(['', 'Invoice', 'SessionBooking'] as const).map(s => (
            <button
              key={s || 'all'}
              onClick={() => setSourceFilter(s)}
              className={`px-3 py-1.5 text-xs font-medium rounded-full border cursor-pointer transition-colors ${
                sourceFilter === s ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
              }`}
            >
              {s === '' ? 'All' : SOURCE_LABEL[s] + 's'}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <Th label="Date"        col="createdAt"   sortKey={sortKey} dir={sortDir} onSort={handleSort} />
                <Th label="Description" col="description" sortKey={sortKey} dir={sortDir} onSort={handleSort} />
                <Th label="Payer"       col="payer"       sortKey={sortKey} dir={sortDir} onSort={handleSort} />
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Type</th>
                <Th label="Amount"      col="amount"      sortKey={sortKey} dir={sortDir} onSort={handleSort} className="text-right" />
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading && <tr><td colSpan={7} className="text-center text-gray-500 py-12">Loading…</td></tr>}
              {!isLoading && filtered.length === 0 && (
                <tr><td colSpan={7} className="text-center text-gray-400 py-12">
                  {payments.length === 0 ? 'No payments yet.' : 'No payments match your filters.'}
                </td></tr>
              )}
              {paged.map(p => {
                const st = refundState(p)
                const remaining = p.amount - p.refundedAmount
                return (
                  <tr key={p.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{fmtDate(p.createdAt)}</td>
                    <td className="px-4 py-3 font-medium text-gray-900">{p.description}</td>
                    <td className="px-4 py-3 text-gray-600">{p.payer ?? '—'}</td>
                    <td className="px-4 py-3 text-gray-500">{SOURCE_LABEL[p.source] ?? p.source}</td>
                    <td className="px-4 py-3 text-right font-medium text-gray-900 whitespace-nowrap">
                      {formatMoney(p.amount, p.currency)}
                      {p.refundedAmount > 0 && (
                        <span className="block text-xs text-amber-600">−{formatMoney(p.refundedAmount, p.currency)} refunded</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${st.cls}`}>
                        {st.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {remaining > 0 && (
                        <button
                          onClick={() => setRefundTarget(p)}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md border border-gray-200 text-gray-700 hover:bg-gray-50 cursor-pointer"
                        >
                          <RotateCcw size={12} /> Refund
                        </button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        <TablePagination page={page} totalPages={totalPages} total={total} pageSize={pageSize} start={start} onPage={setPage} />
      </div>

      {refundTarget && (
        <RefundModal
          payment={refundTarget}
          onClose={() => setRefundTarget(null)}
          onDone={() => { qc.invalidateQueries({ queryKey: ['payments'] }); setRefundTarget(null) }}
        />
      )}
    </div>
  )
}

function RefundModal({ payment, onClose, onDone }: {
  payment: PaymentListDto; onClose: () => void; onDone: () => void
}) {
  const { fmt: money, symbol } = useMoneyFor(payment.currency)
  const remaining = payment.amount - payment.refundedAmount
  const [amount, setAmount] = useState(remaining.toFixed(2))
  const [error, setError] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: (value: number | null) => adminApi.refundPayment(payment.id, value).then(r => r.data),
    onSuccess: onDone,
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { error?: string } }; message?: string }
      setError(err?.response?.data?.error ?? err?.message ?? 'Refund failed')
    },
  })

  const submit = () => {
    setError(null)
    const value = parseFloat(amount)
    if (isNaN(value) || value <= 0 || value > remaining + 0.001) {
      setError(`Enter an amount between ${money(0.01)} and ${money(remaining)}.`)
      return
    }
    // Full remaining refund → send null so the backend refunds the exact balance.
    mutation.mutate(Math.abs(value - remaining) < 0.005 ? null : value)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Refund payment</h2>
        <p className="text-sm text-gray-500 mb-4">{payment.description}{payment.payer ? ` · ${payment.payer}` : ''}</p>

        <div className="rounded-lg bg-gray-50 border border-gray-200 px-4 py-3 text-sm mb-4 space-y-1">
          <div className="flex justify-between"><span className="text-gray-500">Paid</span><span className="font-medium">{money(payment.amount)}</span></div>
          {payment.refundedAmount > 0 && (
            <div className="flex justify-between"><span className="text-gray-500">Already refunded</span><span className="font-medium">{money(payment.refundedAmount)}</span></div>
          )}
          <div className="flex justify-between"><span className="text-gray-500">Refundable</span><span className="font-medium">{money(remaining)}</span></div>
        </div>

        <label className="block text-sm font-medium text-gray-700 mb-1">Refund amount</label>
        <div className="flex items-center gap-2">
          <span className="text-gray-500">{symbol}</span>
          <input
            type="number" min={0} max={remaining} step="0.01"
            value={amount}
            onChange={e => setAmount(e.target.value)}
            className="w-32 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          <button
            type="button"
            onClick={() => setAmount(remaining.toFixed(2))}
            className="text-xs text-indigo-600 hover:underline cursor-pointer"
          >
            Full refund
          </button>
        </div>

        <p className="text-xs text-gray-400 mt-2">
          Refunds come from your Stripe balance. Stripe does not return the original processing fee on a refund.
        </p>

        {error && <p className="text-sm text-red-600 mt-3">{error}</p>}

        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 cursor-pointer">Cancel</button>
          <button
            onClick={submit}
            disabled={mutation.isPending}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60 cursor-pointer"
          >
            {mutation.isPending ? <><Loader2 size={14} className="animate-spin" /> Refunding…</> : 'Refund'}
          </button>
        </div>
      </div>
    </div>
  )
}
